import { BadgeCheck, ChevronDown, FileWarning, Fingerprint } from 'lucide-react'
import { useStudioLocale } from '@/i18n'
import { cn } from '@/lib/utils'
import type { GeneratedArtifactIdentity } from './generated-artifact-identity'

const COPY = {
  en: {
    saved: 'Saved artifact', uncommitted: 'Uncommitted manual revision', browserLocal: 'Browser-local result', session: 'Session-only result', revision: 'Project revision',
    evidence: 'Generation evidence', noEvidence: 'No applied Design System or UI Block evidence is recorded.',
    designSystem: 'Design System', theme: 'Generation theme', tokens: 'Token refs', blocks: 'UI Block refs',
    artifact: 'Artifact', artifactRevision: 'Artifact revision', contentHash: 'Content hash', request: 'Request / run', owner: 'Bot owner', receiptEvidence: 'Receipt evidence',
    spec: 'AppSpec authority', trace: 'Generation trace', hashes: 'Release / binding proof', edit: 'Edit page', uncommittedDetail: 'These project bytes are not bound to a verified generated-artifact child receipt. They are shown as a manual revision, not generated lineage.', browserLocalDetail: 'This result is kept in this browser, not on the server. Browser storage may be cleared.', sessionDetail: 'This result is not stored on the server and can disappear when this session ends.',
  },
  ko: {
    saved: '저장된 결과', uncommitted: '커밋되지 않은 수동 리비전', browserLocal: '브라우저 로컬 결과', session: '세션 전용 결과', revision: '프로젝트 리비전',
    evidence: '생성 근거', noEvidence: '적용된 Design System 또는 UI Block 근거가 기록되지 않았습니다.',
    designSystem: 'Design System', theme: '생성 테마', tokens: '토큰 참조', blocks: 'UI Block 참조',
    artifact: 'Artifact', artifactRevision: 'Artifact 리비전', contentHash: '콘텐츠 해시', request: '요청 / 실행', owner: 'Bot 소유자', receiptEvidence: '영수증 근거',
    spec: 'AppSpec 권한', trace: '생성 추적', hashes: '릴리스 / 바인딩 근거', edit: '페이지 편집', uncommittedDetail: '현재 프로젝트 바이트는 검증된 generated-artifact 자식 영수증과 결합되지 않았습니다. 생성 계보가 아닌 수동 리비전으로 표시합니다.', browserLocalDetail: '서버가 아니라 이 브라우저에 보관됩니다. 브라우저 저장소를 지우면 사라질 수 있습니다.', sessionDetail: '서버에 저장되지 않아 이 세션이 끝나면 사라질 수 있습니다.',
  },
  ja: {
    saved: '保存済み成果物', uncommitted: '未コミットの手動リビジョン', browserLocal: 'ブラウザー内の結果', session: 'セッション限定の結果', revision: 'プロジェクトリビジョン',
    evidence: '生成エビデンス', noEvidence: '適用された Design System または UI Block のエビデンスは記録されていません。',
    designSystem: 'Design System', theme: '生成テーマ', tokens: 'トークン参照', blocks: 'UI Block 参照',
    artifact: '成果物', artifactRevision: '成果物リビジョン', contentHash: 'コンテンツハッシュ', request: 'リクエスト / 実行', owner: 'Bot 所有者', receiptEvidence: 'レシートエビデンス',
    spec: 'AppSpec オーソリティ', trace: '生成トレース', hashes: 'リリース / バインディング証明', edit: 'ページを編集', uncommittedDetail: '現在のプロジェクトデータは、検証済みの生成成果物の子レシートに結び付けられていません。生成系譜ではなく、手動リビジョンとして表示されます。', browserLocalDetail: 'この結果はサーバーではなく、このブラウザー内に保存されています。ブラウザーのストレージを消去すると失われる場合があります。', sessionDetail: 'この結果はサーバーに保存されないため、セッションが終了すると失われる場合があります。',
  },
  zh: {
    saved: '已保存产物', uncommitted: '未提交的手动修订', browserLocal: '浏览器本地结果', session: '仅限会话的结果', revision: '项目修订',
    evidence: '生成证据', noEvidence: '尚未记录已应用的 Design System 或 UI Block 证据。',
    designSystem: 'Design System', theme: '生成主题', tokens: '令牌引用', blocks: 'UI Block 引用',
    artifact: '产物', artifactRevision: '产物修订', contentHash: '内容哈希', request: '请求 / 运行', owner: 'Bot 所有者', receiptEvidence: '回执证据',
    spec: 'AppSpec 权限依据', trace: '生成追踪', hashes: '发布 / 绑定证明', edit: '编辑页面', uncommittedDetail: '当前项目数据未绑定到经过验证的生成产物子回执。系统将其显示为手动修订，而不是生成谱系。', browserLocalDetail: '此结果保存在当前浏览器中，而非服务器上。清除浏览器存储后可能会丢失。', sessionDetail: '此结果未保存在服务器上，会话结束后可能会丢失。',
  },
  'zh-TW': {
    saved: '已儲存成品', uncommitted: '未提交的手動修訂', browserLocal: '瀏覽器本機結果', session: '僅限工作階段的結果', revision: '專案修訂',
    evidence: '生成證據', noEvidence: '尚未記錄已套用的 Design System 或 UI Block 證據。',
    designSystem: 'Design System', theme: '生成主題', tokens: '權杖參照', blocks: 'UI Block 參照',
    artifact: '成品', artifactRevision: '成品修訂', contentHash: '內容雜湊', request: '要求 / 執行', owner: 'Bot 擁有者', receiptEvidence: '收據證據',
    spec: 'AppSpec 權限依據', trace: '生成追蹤', hashes: '發佈 / 綁定證明', edit: '編輯頁面', uncommittedDetail: '目前的專案資料未綁定至經過驗證的生成成品子收據。系統會將其顯示為手動修訂，而非生成譜系。', browserLocalDetail: '此結果儲存在目前的瀏覽器中，而非伺服器上。清除瀏覽器儲存空間後可能會遺失。', sessionDetail: '此結果未儲存在伺服器上，工作階段結束後可能會遺失。',
  },
} as const

function short(value: string): string {
  return value.length > 22 ? `${value.slice(0, 10)}…${value.slice(-8)}` : value
}

export function GeneratedArtifactIdentityChip({
  identity,
  className,
  onEdit,
}: {
  identity: GeneratedArtifactIdentity
  className?: string
  onEdit?: () => void
}) {
  const { locale } = useStudioLocale()
  const copy = COPY[locale]
  const hasGenerationEvidence = Boolean(
    identity.artifactId || identity.traceId || identity.appSpecRef || identity.designSystem || identity.theme || identity.tokenRefs.length || identity.uiBlockRefs.length || identity.evidenceRefs.length,
  )
  const persistenceLabel = identity.persistence === 'saved'
    ? copy.saved
    : identity.persistence === 'uncommitted-local'
      ? copy.uncommitted
      : identity.persistence === 'browser-local'
        ? copy.browserLocal
        : copy.session
  const persistenceDetail = identity.persistence === 'uncommitted-local'
    ? copy.uncommittedDetail
    : identity.persistence === 'browser-local'
      ? copy.browserLocalDetail
      : identity.persistence === 'session-only'
        ? copy.sessionDetail
        : null

  return (
    <details
      className={cn('group max-w-[min(30rem,calc(100vw-2rem))] rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur', className)}
      data-testid="generated-artifact-identity"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-2.5 py-2 text-xs text-foreground">
        {identity.persistence === 'saved' ? (
          <BadgeCheck className="size-4 shrink-0 text-success" aria-hidden />
        ) : (
          <FileWarning className="size-4 shrink-0 text-amber-600" aria-hidden />
        )}
        <span className="font-medium">
          {persistenceLabel}
        </span>
        {identity.artifactId ? (
          <code className="min-w-0 truncate font-mono text-[11px] text-muted-foreground" title={identity.artifactId}>
            {short(identity.artifactId)}
          </code>
        ) : null}
        {identity.currentProjectRevision !== null ? (
          <span className="shrink-0 text-muted-foreground">{copy.revision} {identity.currentProjectRevision}</span>
        ) : null}
        <ChevronDown className="ml-auto size-3.5 shrink-0 transition-transform group-open:rotate-180" aria-hidden />
      </summary>

      <div className="border-t border-border px-3 py-3 text-xs">
        {persistenceDetail ? (
          <p className="mb-3 rounded-md bg-amber-500/10 px-2.5 py-2 leading-5 text-amber-800 dark:text-amber-200">
            {persistenceDetail}
          </p>
        ) : null}
        <div className="mb-2 flex items-center gap-1.5 font-medium text-foreground">
          <Fingerprint className="size-3.5" aria-hidden />
          {copy.evidence}
        </div>
        {hasGenerationEvidence ? (
          <dl className="grid gap-2 text-muted-foreground">
            {identity.artifactId ? <EvidenceRow label={copy.artifact} value={identity.artifactId} mono /> : null}
            {identity.revision ? <EvidenceRow label={copy.artifactRevision} value={identity.revision} mono /> : null}
            {identity.contentHash ? <EvidenceRow label={copy.contentHash} value={identity.contentHash} mono /> : null}
            {identity.requestId && identity.runId ? <EvidenceRow label={copy.request} value={`${identity.requestId} · ${identity.runId}`} mono /> : null}
            {identity.ownerBotProfileId ? <EvidenceRow label={copy.owner} value={identity.ownerBotProfileId} mono /> : null}
            {identity.evidenceRefs.length ? <EvidenceRow label={copy.receiptEvidence} value={identity.evidenceRefs.map((ref) => `${ref.ref}#${ref.contentHash}`).join(', ')} mono /> : null}
            {identity.designSystem ? (
              <>
                <EvidenceRow label={copy.designSystem} value={`${identity.designSystem.packageLabel} · ${identity.designSystem.releaseId}`} />
                <EvidenceRow label={copy.hashes} value={`${identity.designSystem.snapshotHash} · ${identity.designSystem.bindingHash}`} mono />
              </>
            ) : null}
            {identity.theme ? (
              <EvidenceRow
                label={copy.theme}
                value={[identity.theme.style, identity.theme.primaryColor, identity.theme.fontFamily].filter(Boolean).join(' · ') || '—'}
              />
            ) : null}
            {identity.tokenRefs.length ? <EvidenceRow label={copy.tokens} value={identity.tokenRefs.join(', ')} /> : null}
            {identity.uiBlockRefs.length ? <EvidenceRow label={copy.blocks} value={identity.uiBlockRefs.join(', ')} /> : null}
            {identity.appSpecRef ? <EvidenceRow label={copy.spec} value={identity.appSpecRef} mono /> : null}
            {identity.traceId ? <EvidenceRow label={copy.trace} value={identity.traceId} mono /> : null}
          </dl>
        ) : (
          <p className="leading-5 text-muted-foreground">{copy.noEvidence}</p>
        )}
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="mt-3 inline-flex h-8 items-center rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            {copy.edit}
          </button>
        ) : null}
      </div>
    </details>
  )
}

function EvidenceRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2">
      <dt>{label}</dt>
      <dd className={cn('break-all text-foreground', mono && 'font-mono text-[11px]')}>{value}</dd>
    </div>
  )
}
